import { popupTypes } from 'helpers/constants';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';


import { GeneralDialog, GeneralButton } from 'sharedComponents';
import { BulkUploadFileSection } from './BulkUploadFileSection';
import { BulkUploadStatusSection } from './BulkUploadStatusSection';
import { checkUploadedFiles, getBulkUploadDeviceTypeLabel } from './helpers';
import { formatDateForApi } from './BulkUploadStatusSection/helpers';

import './style.scss';


const BulkUploadComponent = ({ show, close, model, actions }) => {
    const { t: translate } = useTranslation();
    const [fileUploadWarningVisibility, setFileUploadWarningVisibility] = useState(false);
    const [fileUploadInfoVisibility, setFileUploadInfoVisibility] = useState(false);
    const bulkUploadDeviceTypeLabel = getBulkUploadDeviceTypeLabel(model?.selectedAsset);

    const files = useSelector(({ bulkUploadV2 }) => {
        return {
            isFiles: bulkUploadV2 && bulkUploadV2.bulkFiles
                && bulkUploadV2.bulkFiles.length ? true : false,
            isUploadFinished: checkUploadedFiles(bulkUploadV2.bulkFiles)
        };
    });

    const showProcessWaning = () => {
        setFileUploadInfoVisibility(true);
    };

    const processBulkUpload = async () => {
        if (model?.bulkFiles && model?.bulkFiles?.length) {
            const bulkUploadFiles = model?.bulkFiles?.map(fileObj => ({
                blobname: fileObj?.blobName,
                filename: fileObj?.fileContent?.name
            }));

            try {
                await actions.startBulkUploadRequest(
                    model?.selectedAsset?.id,
                    bulkUploadFiles,
                    formatDateForApi(model.dateInterval.from),
                    formatDateForApi(model.dateInterval.to)
                );
                actions.clearFileBoxAction();
            } catch (err) {
                console.error('Bulk upload failed for files', err);
            }
        }
        setFileUploadInfoVisibility(false);
        actions.setProcessAllowedAction(false);
    };

    const handleUploadClick = () => {
        actions.uploadFilesV2(model.selectedAsset.id, model.bulkFiles, formatDateForApi(model.dateInterval.from),
            formatDateForApi(model.dateInterval.to));
    };

    return <>
        <GeneralDialog
            modalClassName='dialog-second-level'
            backdropClassName='dialog-second-level-backdrop'
            notificationType={popupTypes.WARNING}
            show={fileUploadWarningVisibility}
            close={() => setFileUploadWarningVisibility(false)}
            title={translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadWarningPopupTitle')}
            cancelButtonProps={{ text: translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadDialogNoLabel') }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadDialogYesLabel'),
                onClick: close
            }}
        >
            {translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadWarningPopupText')}
        </GeneralDialog>
        <GeneralDialog
            modalClassName='dialog-second-level'
            backdropClassName='dialog-second-level-backdrop'
            notificationType={popupTypes.INFO}
            show={fileUploadInfoVisibility}
            close={() => setFileUploadInfoVisibility(false)}
            title={translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadInfoPopupTitle')}
            cancelButtonProps={{ text: translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadDialogNoLabel') }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadDialogYesLabel'),
                onClick: processBulkUpload
            }}
        >
            {translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadInfoPopupText')}
        </GeneralDialog>
        <GeneralDialog
            show={show}
            close={() => { files.isFiles ? setFileUploadWarningVisibility(true) : close(); }}
            title={<span><strong>{translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadModalTitle')} &ldquo;{model?.selectedAsset?.name}&rdquo;</strong></span>}
            closeButton={true}
            dialogClassName='bulk-upload-dialog'
        >
            <BulkUploadFileSection />
            <div className='note-buttons-container'>
                <div className='bulk-upload-note'>
                    {translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadNote', { deviceType: bulkUploadDeviceTypeLabel })}
                </div>
                <div className='buttons-row'>
                    <GeneralButton
                        onClick={handleUploadClick}
                        disabled={!model?.bulkFiles?.length || !model?.isUploadAllowed || model?.isUploading || model?.isProcessing}
                        type='normal'
                        text={translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadButton')}
                    />
                    <GeneralButton
                        onClick={showProcessWaning}
                        disabled={!model?.isProcessAllowed || model?.isUploading || !files.isUploadFinished || !files.isFiles}
                        type='primary'
                        text={translate('ABB.Powertrain.Frontend.detailedInformationBulkUploadProcessButton')}
                    />
                </div>
            </div>
            <BulkUploadStatusSection />
        </GeneralDialog>
    </>;
};

BulkUploadComponent.propTypes = {
    show: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    model: PropTypes.shape({
        selectedAsset: PropTypes.object,
        bulkFiles: PropTypes.array,
        dateInterval: PropTypes.object,
        isUploadAllowed: PropTypes.bool,
        isUploading: PropTypes.bool,
        isProcessAllowed: PropTypes.bool,
        isProcessing: PropTypes.bool
    }).isRequired,
    actions: PropTypes.shape({
        getBulkUploadSasUrlRequest: PropTypes.func.isRequired,
        startBulkUploadRequest: PropTypes.func.isRequired,
        getBulkUploadStatusRequest: PropTypes.func.isRequired
    }).isRequired
};

export default BulkUploadComponent;
